import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePayAmigoSession, usePayAmigoForm } from '../../hooks';
import { PaymentType, PaymentStatus } from '../../api/payamigo';

import Section from '../section';
import Button from '../UI/button';
import PixQrCode from '../pixQrCode';
import PaymentStatusComponent from '../paymentStatus';
import PayAmigoFormFields from '../payAmigoFormFields';
import QuoteSummary from '../quoteSummary';

import './pay-amigo-form.scss';

interface IPayAmigoFormProps {
  existingSessionId?: string;
  coin?: string;
  paymentType?: PaymentType;
}

const PayAmigoForm: React.FC<IPayAmigoFormProps> = ({
  existingSessionId,
  coin = 'BRL',
  paymentType = PaymentType.Deposit,
}) => {
  const { t } = useTranslation();

  // Session management
  const { sessionId } = usePayAmigoSession(existingSessionId);

  // Form management - вся логіка в хуку
  const {
    // Coins
    coins,
    coinsLoading,
    coinsLoadingComplete,
    selectedCoin,
    
    // Form fields
    cpf,
    cpfError,
    amount,
    amountError,
    pixKey,
    pixKeyError,
    
    // Handlers
    handleCpfChange,
    handleAmountChange,
    handlePixKeyChange,
    handleCoinChange,
    
    // Quote
    quote,
    quoteLoading,
    quoteError,
    
    // Payment
    executeResult,
    currentStatus,
    isExecuting,
    executeError,
    handleExecutePayment,
    
    // Validation
    isFormValid,
    isPaymentComplete,
    isPaymentFailed,
  } = usePayAmigoForm({
    sessionId,
    paymentType,
    initialCoin: coin,
  });

  // Show loading while fetching coins
  if (coinsLoading || !coinsLoadingComplete) {
    return (
      <div className="pay-amigo-form">
        <Section
          className="pay-amigo-form-section"
          title={t('PAY_AMIGO.TITLE', { defaultValue: 'Payment Details' })}
          isLoading={true}
        >
          <div className="form-fields" />
        </Section>
      </div>
    );
  }

  // Show error if no coins available
  if (coinsLoadingComplete && !coinsLoading && coins.length === 0) {
    return (
      <div className="pay-amigo-form">
        <Section
          className="pay-amigo-form-section"
          title={t('PAY_AMIGO.TITLE', { defaultValue: 'Payment Details' })}
        >
          <div className="unavailable-message">
            <p>{t('PAY_AMIGO.SERVICE_UNAVAILABLE', { defaultValue: 'Service is currently unavailable. Please try again later.' })}</p>
          </div>
        </Section>
      </div>
    );
  }

  // Show success screen using PaymentStatusComponent
  if (isPaymentComplete && executeResult && currentStatus) {
    return (
      <div className="pay-amigo-form">
        <PaymentStatusComponent
          currentStatus={currentStatus}
          executeResult={executeResult}
          isPaymentComplete={isPaymentComplete}
          isPaymentFailed={isPaymentFailed}
        />
      </div>
    );
  }

  return (
    <div className="pay-amigo-form">
      {!executeResult && (
        <Section
          className="pay-amigo-form-section"
          title={t('PAY_AMIGO.TITLE', { defaultValue: 'Payment Details' })}
          isLoading={false}
        >
          <PayAmigoFormFields
            cpf={cpf}
            cpfError={cpfError}
            onCpfChange={handleCpfChange}
            pixKey={pixKey}
            pixKeyError={pixKeyError}
            onPixKeyChange={handlePixKeyChange}
            selectedCoin={selectedCoin}
            coins={coins}
            onCoinChange={handleCoinChange}
            amount={amount}
            amountError={amountError}
            onAmountChange={handleAmountChange}
            paymentType={paymentType}
          />

          {quoteError && (
            <p className="quote-error-message">
              {quoteError}
            </p>
          )}

          {executeError && (
            <p className="execute-error-message">
              {executeError}
            </p>
          )}

          {quote && !executeResult && (
            <QuoteSummary quote={quote} isLoading={quoteLoading} />
          )}

          {!executeResult && (
            <Button
              className="submit-button"
              title={
                quoteLoading
                  ? t('PAY_AMIGO.LOADING_QUOTE', { defaultValue: 'Loading quote...' })
                  : t('PAY_AMIGO.SUBMIT', { defaultValue: 'Continue to Payment' })
              }
              onClick={handleExecutePayment}
              disabled={!isFormValid || quoteLoading}
              pending={isExecuting}
            />
          )}
        </Section>
      )}

      {/* Show QR code only for deposit and only when status hasn't changed yet */}
      {paymentType === PaymentType.Deposit && 
       executeResult && 
       executeResult.status === PaymentStatus.PAYMENT_PENDING && 
       currentStatus === PaymentStatus.PAYMENT_PENDING &&
       'pix_data' in executeResult && (
        <PixQrCode
          pixData={executeResult.pix_data}
          amount={executeResult.underlying_amount || undefined}
          coin={executeResult.underlying_coin || undefined}
        />
      )}

      {/* Show Payment Status only when QR code is not shown */}
      {currentStatus && executeResult && !(
        paymentType === PaymentType.Deposit && 
        executeResult.status === PaymentStatus.PAYMENT_PENDING && 
        currentStatus === PaymentStatus.PAYMENT_PENDING &&
        'pix_data' in executeResult
      ) && (
        <PaymentStatusComponent
          currentStatus={currentStatus}
          executeResult={executeResult}
          isPaymentComplete={isPaymentComplete}
          isPaymentFailed={isPaymentFailed}
        />
      )}
    </div>
  );
};

export default PayAmigoForm;

